'use client';

import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@src/lib/utils';

const switchVariants = cva(
  'peer inline-flex rounded-[100px] p-[2px] transition-all duration-200 ease-smooth outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'data-[state=checked]:bg-focus data-[state=unchecked]:bg-input',
      },
      size: {
        default: 'h-[31px] w-[51px] sm:h-[31px] sm:w-[51px]',
        sm: 'h-[24px] w-[42px] sm:h-[24px] sm:w-[42px]',
        md: 'h-[28px] w-[48px] sm:h-[28px] sm:w-[54px]',
        lg: 'h-[38px] w-[62px] sm:h-[38px] sm:w-[62px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

const thumbVariants = cva(
  'pointer-events-none block rounded-full bg-background shadow-sm will-change-transform transition-transform duration-200 ease-smooth',
  {
    variants: {
      size: {
        default: 'h-[27px] w-[27px] sm:h-[27px] sm:w-[27px]',
        sm: 'h-[20px] w-[20px] sm:h-[20px] sm:w-[20px]',
        md: 'h-[24px] w-[24px] sm:h-[24px] sm:w-[24px]',
        lg: 'h-[34px] w-[34px] sm:h-[34px] sm:w-[34px]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, variant, size, ...props }, ref) => {
  return (
    <SwitchPrimitive.Root
      className={cn(
        switchVariants({ variant, size }),
        'data-[state=checked]:justify-end data-[state=unchecked]:justify-start',
        className
      )}
      ref={ref}
      data-slot="switch"
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(thumbVariants({ size }))}
      />
    </SwitchPrimitive.Root>
  );
});

Switch.displayName = 'Switch';

export { Switch, switchVariants, thumbVariants };
